import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import ContactForm from '../components/forms/ContactForm';
import { Breadcrumbs } from '../components/Breadcrumbs';

const KruPage: React.FC = () => {
  const pageTitle =
    'КРУ — комплектное распределительное устройство: расшифровка, типы, отличия от КСО';
  const pageDescription =
    'Расшифровка, назначение, основные элементы и типы КРУ. Отличия от КСО и области применения на подстанциях 6–35 кВ.';
  const imageUrl = '/images/blog/kru-featured.jpg'; // Placeholder

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://prominnovations.ru/kru" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={`https://prominnovations.ru${imageUrl}`} />
        <meta property="og:url" content="https://prominnovations.ru/kru" />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className="font-museo-sans">
        <div className="bg-[#f9fcff]">
          <div className="container-custom mx-auto pt-2 lg:pt-5">
            <Breadcrumbs />
          </div>
        </div>

        <main>
          <section className="bg-[#f9fcff]">
            <div className="container-custom mx-auto">
              <h1 className="text-4xl lg:text-5xl font-bold mb-5 bg-gradient-to-r from-[#1a3a4f] to-[#2b5c7a] text-transparent bg-clip-text">
                КРУ — комплектное распределительное устройство
              </h1>
              <p className="text-xl text-[#2c5a7a] max-w-4xl">
                Расшифровка, назначение, основные элементы и типы КРУ. Отличия от КСО и области
                применения на подстанциях 6–35 кВ.
              </p>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Что такое КРУ?
              </h2>
              <div className="bg-blue-50 border-l-4 border-blog-accent p-8 rounded-r-2xl my-6">
                <p>
                  <strong className="text-blog-accent font-semibold">
                    КРУ (комплектное распределительное устройство)
                  </strong>{' '}
                  — это электротехническое устройство заводского изготовления, предназначенное для
                  приёма и распределения электрической энергии напряжением <strong>6–35 кВ</strong>,
                  а также для защиты линий и оборудования от токов короткого замыкания и перегрузок.
                </p>
                <p className="mt-4">
                  <em>Простыми словами:</em> это набор металлических шкафов (ячеек) с выключателями,
                  разъединителями, трансформаторами тока и релейной защитой, которые собираются в
                  единый блок и поставляются на объект «под ключ». В отличие от открытых
                  распределительных устройств (ОРУ), КРУ компактны и безопасны.
                </p>
                <p className="mt-4">
                  КРУ широко применяется на трансформаторных подстанциях (ТП, КТПН), в
                  распределительных пунктах (РП), на промышленных предприятиях и в энергосистемах.
                </p>
              </div>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Расшифровка аббревиатуры
              </h2>
              <div className="flex flex-wrap gap-5 my-5">
                <div className="flex-1 bg-white rounded-2xl p-5 border border-gray-200 min-w-[220px]">
                  <i className="fas fa-cubes text-blog-accent text-2xl mb-4"></i>
                  <h3 className="font-bold text-lg">К — Комплектное</h3>
                  <p className="text-gray-600">
                    Изготовлено заводским способом в виде готовых шкафов, не требует сборки на месте
                    из отдельных деталей.
                  </p>
                </div>
                <div className="flex-1 bg-white rounded-2xl p-5 border border-gray-200 min-w-[220px]">
                  <i className="fas fa-random text-blog-accent text-2xl mb-4"></i>
                  <h3 className="font-bold text-lg">Р — Распределительное</h3>
                  <p className="text-gray-600">
                    Предназначено для распределения электроэнергии по нескольким отходящим линиям.
                  </p>
                </div>
                <div className="flex-1 bg-white rounded-2xl p-5 border border-gray-200 min-w-[220px]">
                  <i className="fas fa-microchip text-blog-accent text-2xl mb-4"></i>
                  <h3 className="font-bold text-lg">У — Устройство</h3>
                  <p className="text-gray-600">
                    Техническое изделие, выполняющее функции коммутации, измерения, защиты.
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Назначение и области применения
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 my-5">
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <i className="fas fa-arrow-left text-2xl text-blog-accent mb-4"></i>
                  <h3 className="font-bold text-lg">Приём электроэнергии</h3>
                  <p className="text-gray-600">
                    Получение питания от трансформатора или ввода от электростанции.
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <i className="fas fa-arrow-right text-2xl text-blog-accent mb-4"></i>
                  <h3 className="font-bold text-lg">Распределение</h3>
                  <p className="text-gray-600">
                    Направление энергии на несколько отходящих фидеров (кабельные или воздушные
                    линии).
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <i className="fas fa-shield-alt text-2xl text-blog-accent mb-4"></i>
                  <h3 className="font-bold text-lg">Защита от КЗ</h3>
                  <p className="text-gray-600">
                    Автоматическое отключение повреждённых участков (выключатели, релейная защита).
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <i className="fas fa-tachometer-alt text-2xl text-blog-accent mb-4"></i>
                  <h3 className="font-bold text-lg">Контроль и измерение</h3>
                  <p className="text-gray-600">
                    Трансформаторы тока и напряжения для учёта и мониторинга.
                  </p>
                </div>
              </div>
              <p>
                <strong>Где устанавливается КРУ:</strong> городские и заводские подстанции (ТП, РП),
                КТПН (в шкафу НН или в отдельном отсеке 10 кВ), гидро- и теплоэлектростанции,
                объекты нефтегазовой отрасли. Напряжение — от 6 до 35 кВ включительно.
              </p>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Из чего состоит КРУ?
              </h2>
              <div className="flex flex-wrap gap-5 my-5">
                <div className="flex-1 bg-[#f9fcff] rounded-2xl p-5">
                  <i className="fas fa-bolt mr-2"></i> <strong>Силовые выключатели</strong>
                  <br />
                  Вакуумные или элегазовые — для отключения тока КЗ.
                </div>
                <div className="flex-1 bg-[#f9fcff] rounded-2xl p-5">
                  <i className="fas fa-code-branch mr-2"></i>{' '}
                  <strong>Разъединители, заземляющие ножи</strong>
                  <br />
                  Создание видимого разрыва и заземление.
                </div>
                <div className="flex-1 bg-[#f9fcff] rounded-2xl p-5">
                  <i className="fas fa-chart-line mr-2"></i>{' '}
                  <strong>Трансформаторы тока (ТТ) и напряжения (ТН)</strong>
                  <br />
                  Для измерений и защиты.
                </div>
                <div className="flex-1 bg-[#f9fcff] rounded-2xl p-5">
                  <i className="fas fa-microchip mr-2"></i>{' '}
                  <strong>Устройства релейной защиты (РЗА)</strong>
                  <br />
                  Реле, контроллеры, терминалы.
                </div>
              </div>
              <p>
                Всё это смонтировано в металлическом шкафу (ячейке) со степенью защиты от IP20 до
                IP54. Шкафы собираются в ряд, соединяются общими сборными шинами.
              </p>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Основные типы КРУ
              </h2>
              <ul className="flex flex-wrap gap-4 my-5 list-none">
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>КРУ внутренней установки</strong> — для сухих отапливаемых помещений
                  (электрощитовые, ТП внутри зданий).
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>КРУН</strong> — наружной установки (в контейнере или специальном корпусе с
                  подогревом).
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>КРУЭ</strong> — элегазовое (с элегазовой изоляцией), для сверхвысоких
                  напряжений 110 кВ и выше, но также выпускается на 35 кВ.
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>КРУ с выкатными элементами</strong> — возможность замены выключателя без
                  снятия напряжения с шин.
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>КРУ стационарного исполнения</strong> — все аппараты закреплены
                  стационарно.
                </li>
              </ul>
              <p>
                Наиболее распространены в сетях 6–35 кВ{' '}
                <strong>КРУ с вакуумными выключателями</strong> и выкатными тележками (серия К-ХХ,
                КРУ-10 и др.).
              </p>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Чем КРУ отличается от КСО?
              </h2>
              <div className="bg-gray-50 rounded-2xl p-6 my-5">
                <p>
                  <strong className="text-blog-accent font-semibold">
                    КСО (камера сборная одностороннего обслуживания)
                  </strong>{' '}
                  — это упрощённый тип распределительного устройства, в котором обслуживание (доступ
                  к выключателям, разъединителям) производится только спереди. КСО дешевле, но имеет
                  ограничения по безопасности и удобству обслуживания.
                </p>
                <p className="mt-4">
                  <strong className="text-blog-accent font-semibold">
                    Основные отличия КРУ от КСО:
                  </strong>
                </p>
                <ul className="mt-3 space-y-2">
                  <li>
                    <i className="fas fa-check-circle text-blog-accent mr-2"></i> КРУ —
                    двухстороннее обслуживание (доступ с тыльной стороны), КСО — одностороннее.
                  </li>
                  <li>
                    <i className="fas fa-check-circle text-blog-accent mr-2"></i> КРУ имеет более
                    высокие показатели электробезопасности (механические блокировки, заземляющие
                    ножи).
                  </li>
                  <li>
                    <i className="fas fa-check-circle text-blog-accent mr-2"></i> КСО обычно
                    применяется на вводах 6–10 кВ в небольших ТП и РП; КРУ — для более ответственных
                    узлов (питание целых районов, крупные заводы).
                  </li>
                </ul>
                <p className="mt-4">
                  <i className="fas fa-arrow-right text-blog-accent mr-2"></i>{' '}
                  <Link to="/catalog/kso" className="text-blue-600 hover:underline">
                    Перейти к глоссарию «КСО»
                  </Link>
                </p>
              </div>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Связанные термины
              </h2>
              <div className="flex flex-wrap gap-4 my-5">
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link to="/catalog/kso" className="font-semibold text-blue-600 hover:underline">
                    КСО
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">
                    Камера сборная одностороннего обслуживания
                  </span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link to="/ktpn-kiosk" className="font-semibold text-blue-600 hover:underline">
                    КТПН
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">Трансформаторная подстанция</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link
                    to="/catalog/transformers"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    Трансформатор силовой
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">Источник питания для КРУ</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link
                    to="/nominalnoe-napryazhenie"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    Номинальное напряжение
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">6, 10, 35 кВ</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link to="/nku" className="font-semibold text-blue-600 hover:underline">
                    НКУ
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">
                    Низковольтные комплектные устройства
                  </span>
                </div>
              </div>
              <div className="mt-10">
                <Link to="/ktpn-100" className="btn-secondary">
                  <i className="fas fa-arrow-left"></i> КТПН с КРУ 10 кВ
                </Link>
                <Link to="/blog/chto-takoe-stepen-zashchity-ip" className="btn-secondary ml-4">
                  <i className="fas fa-shield-alt"></i> Степень защиты IP
                </Link>
              </div>
            </div>
          </section>
        </main>

        <div className="container-custom mx-auto pb-5 lg:pb-10">
          <ContactForm />
        </div>
      </div>
    </>
  );
};

export default KruPage;
